import React, { useEffect, useMemo, useState } from "react";
import {
  clearToken,
  createContact,
  createGroup,
  createInvite,
  getToken,
  joinGroup,
  leaveGroup,
  updateContact,
  listFriends,
  listGroups,
  listGroupExpenses,
  listContacts,
  login,
  me,
  register,
  setToken,
  uploadFile,
  deleteContact,
  getGroupBalances,
  type FriendResponse,
  type ContactResponse,
  type GroupResponse,
  type ExpenseResponse,
  type InviteCreateResponse,
  type MeResponse,
} from "./api";
import { GroupPage } from "./GroupPage";
import { SwipeRow } from "./SwipeRow";
import { ProfilePage } from "./ProfilePage";
import { SupportPage } from "./SupportPage";
import { PrivacyPage } from "./PrivacyPage";
import { AuthedImage } from "./AuthedImage";
import { t, type Lang } from "./i18n";
import { formatMoney } from "./money";
import { useIsMobileTouch } from "./useIsMobileTouch";
import buildInfo from "./buildInfo";

type Route = "login" | "register" | "join-group" | "dashboard" | "group" | "profile" | "support" | "privacy";
type Tab = "groups" | "friends" | "activity" | "account";

export function App() {
  const [path, setPath] = useState<string>(() => `${window.location.pathname}${window.location.search}`);
  const url = new URL(path, window.location.origin);
  const tokenFromUrl = url.searchParams.get("token") || "";
  const groupIdFromPath = url.pathname.startsWith("/groups/") ? url.pathname.split("/")[2] || "" : "";
  const isProfilePath = url.pathname === "/profile";
  const initialTab: Tab = (() => {
    if (url.pathname.startsWith("/friends")) return "friends";
    if (url.pathname.startsWith("/activity")) return "activity";
    if (url.pathname.startsWith("/account") || isProfilePath) return "account";
    return "friends";
  })();

  const languageOptions: Array<{ code: string; label: string }> = [
    { code: "en", label: "English" },
    { code: "ru", label: "Русский" },
  ];
  const currencyOptions = ["USD", "EUR", "GBP", "JPY", "RUB", "CNY", "AUD", "CAD", "CHF", "SEK"];

  const [authToken, setAuthToken] = useState<string | null>(() => getToken());
  const [theme, setTheme] = useState<"light" | "dark">(
    () => (localStorage.getItem("swk_theme") as "light" | "dark") || "light",
  );
  const [preferredLanguage, setPreferredLanguage] = useState(
    () => localStorage.getItem("swk_lang") || languageOptions[0].code,
  );
  const [preferredCurrency, setPreferredCurrency] = useState(
    () => localStorage.getItem("swk_currency") || currencyOptions[0],
  );
  const [displayName, setDisplayName] = useState(() => localStorage.getItem("swk_display_name") || "");
  const lang: Lang = (preferredLanguage === "ru" ? "ru" : "en") as Lang;
  const route: Route = (() => {
    if (url.pathname === "/privacy") return "privacy";
    if (url.pathname === "/support") return "support";
    if (url.pathname === "/register") return "register";
    if (url.pathname === "/join-group") return "join-group";
    if (url.pathname.startsWith("/groups/") && groupIdFromPath) return authToken ? "group" : "login";
    if (isProfilePath) return authToken ? "profile" : "login";
    return authToken ? "dashboard" : "login";
  })();

  function navigate(to: string) {
    window.history.pushState({}, "", to);
    setPath(to);
    setMenuOpen(false);
    if (to.startsWith("/friends")) setCurrentTab("friends");
    else if (to.startsWith("/activity")) setCurrentTab("activity");
    else if (to.startsWith("/account") || to === "/profile") setCurrentTab("account");
    else if (to.startsWith("/groups")) setCurrentTab("groups");
    else setCurrentTab("friends");
  }

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteToken, setInviteToken] = useState(tokenFromUrl);
  const [joinToken, setJoinToken] = useState(tokenFromUrl);
  const [status, setStatus] = useState<string | null>(null);
  const [meId, setMeId] = useState<string | null>(null);
  const [meEmail, setMeEmail] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [inviteResult, setInviteResult] = useState<InviteCreateResponse | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [groups, setGroups] = useState<GroupResponse[] | null>(null);
  const [friends, setFriends] = useState<FriendResponse[] | null>(null);
  const [newGroupName, setNewGroupName] = useState("My group");
  const [newGroupCurrency, setNewGroupCurrency] = useState(currencyOptions[0]);
  const [newGroupDescription, setNewGroupDescription] = useState("");
  const [newGroupAvatarId, setNewGroupAvatarId] = useState<string | null>(null);
  const [newGroupMembers, setNewGroupMembers] = useState<Record<string, boolean>>({});
  const [newContactEmail, setNewContactEmail] = useState("");
  const [newContactPhone, setNewContactPhone] = useState("");
  const [newContactLocalName, setNewContactLocalName] = useState("");
  const [contactEditModal, setContactEditModal] = useState<{
    id: string | null;
    name: string;
    phone: string;
    email: string;
  }>({ id: null, name: "", phone: "", email: "" });
  const [contactSaving, setContactSaving] = useState<Record<string, boolean>>({});
  const [meData, setMeData] = useState<MeResponse | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [contacts, setContacts] = useState<ContactResponse[] | null>(null);
  const [currentTab, setCurrentTab] = useState<Tab>(initialTab);
  const [activityItems, setActivityItems] = useState<
    Array<ExpenseResponse & { group_name: string; occurred_at: string }>
  >([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [showFriendForm, setShowFriendForm] = useState(false);
  const [groupSearch, setGroupSearch] = useState("");
  const [friendSearch, setFriendSearch] = useState("");
  const [groupExpenseTrigger, setGroupExpenseTrigger] = useState(0);
  const [expensePickerOpen, setExpensePickerOpen] = useState(false);
  const [expensePickerTab, setExpensePickerTab] = useState<"friends" | "groups">("friends");
  const [balanceByGroup, setBalanceByGroup] = useState<Record<string, { net: number; currency: string }>>({});
  const isMobileTouch = useIsMobileTouch();
  const [openGroupSwipe, setOpenGroupSwipe] = useState<{ id: string; side: "left" | "right" } | null>(null);
  const [openContactSwipe, setOpenContactSwipe] = useState<{ id: string; side: "left" | "right" } | null>(null);

  const visibleContacts = useMemo(() => {
    return (contacts ?? []).filter((c) => !c.contact_user_id || c.id !== c.contact_user_id);
  }, [contacts]);

  const filteredGroups = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    if (!q) return groups ?? [];
    return (groups ?? []).filter((g) => {
      const hay = `${g.name || ""} ${g.description || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [groupSearch, groups]);

  const filteredContacts = useMemo(() => {
    const q = friendSearch.trim().toLowerCase();
    if (!q) return visibleContacts;
    return visibleContacts.filter((c) => {
      const hay = `${c.resolved_display_name || ""} ${c.email || ""} ${c.phone || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [friendSearch, visibleContacts]);

  const isRegisteredContact = (contact: ContactResponse) =>
    Boolean(contact.contact_user_id) && contact.contact_is_placeholder === false;
  const isPendingContact = (contact: ContactResponse) =>
    !contact.contact_user_id || contact.contact_is_placeholder === true;

  const activeBalanceGroupIds = useMemo(() => {
    const ids = new Set<string>();
    (groups ?? []).forEach((g) => ids.add(g.id));
    // Include friend ledgers for the overall header sum.
    (friends ?? []).forEach((f) => ids.add(f.group_id));
    visibleContacts.forEach((c) => c.group_id && ids.add(c.group_id));
    if (groupIdFromPath) ids.add(groupIdFromPath);
    return ids;
  }, [groupIdFromPath, groups, friends, visibleContacts]);

  const balanceFetchGroupIds = useMemo(() => {
    const ids = new Set<string>();
    (groups ?? []).forEach((g) => ids.add(g.id));
    visibleContacts.forEach((c) => c.group_id && ids.add(c.group_id));
    if (currentTab === "friends") {
      (friends ?? []).forEach((f) => ids.add(f.group_id));
    }
    if (groupIdFromPath) ids.add(groupIdFromPath);
    return ids;
  }, [groupIdFromPath, groups, friends, visibleContacts, currentTab]);

  const overallByCurrency = useMemo(() => {
    const acc: Record<string, number> = {};
    Object.entries(balanceByGroup).forEach(([gid, b]) => {
      if (!activeBalanceGroupIds.has(gid)) return;
      acc[b.currency] = (acc[b.currency] || 0) + b.net;
    });
    return acc;
  }, [activeBalanceGroupIds, balanceByGroup]);
  const isOverallSettledUp = useMemo(() => {
    const activeBalances = Object.entries(balanceByGroup).filter(([gid]) => activeBalanceGroupIds.has(gid));
    if (activeBalances.length === 0) return false;
    return activeBalances.every(([, balance]) => balance.net === 0);
  }, [activeBalanceGroupIds, balanceByGroup]);

  const cardBg = theme === "dark" ? "#121923" : "#fff";
  const cardShadow = theme === "dark" ? "0 10px 26px rgba(0,0,0,0.35)" : "0 8px 18px rgba(0,0,0,0.04)";
  const cardStyle: React.CSSProperties = {
    background: "var(--swk-card)",
    borderRadius: 14,
    boxShadow: cardShadow,
    padding: 16,
  };
  const labelStyle: React.CSSProperties = { fontSize: 12, color: theme === "dark" ? "#9fb3d1" : "#69727d", fontWeight: 700 };
  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid var(--swk-border)",
    background: "var(--swk-input-bg)",
    color: "var(--swk-input-text)",
  };
  const primaryButton: React.CSSProperties = {
    width: "100%",
    padding: "12px 14px",
    background: "#0f8c57",
    color: "#fff",
    borderRadius: 12,
    border: "none",
    fontWeight: 800,
  };
  const secondaryButton: React.CSSProperties = {
    width: "100%",
    padding: "12px 14px",
    background: "var(--swk-input-bg)",
    color: "#0f8c57",
    borderRadius: 12,
    border: theme === "dark" ? "1px solid #273244" : "1px solid #dde4ec",
    fontWeight: 800,
    textAlign: "center",
    cursor: "pointer",
  };

  useEffect(() => {
    const onPop = () => setPath(`${window.location.pathname}${window.location.search}`);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    document.body.style.background = theme === "dark" ? "#0e1116" : "#f6f8fb";
    document.body.style.color = theme === "dark" ? "#e7ecf3" : "#111";
    localStorage.setItem("swk_theme", theme);

    const root = document.documentElement;
    if (theme === "dark") {
      root.style.setProperty("--swk-bg", "#0f141a");
      root.style.setProperty("--swk-card", "#121923");
      root.style.setProperty("--swk-border", "#273244");
      root.style.setProperty("--swk-muted", "#9fb3d1");
      root.style.setProperty("--swk-input-bg", "#0e1116");
      root.style.setProperty("--swk-input-text", "#e7ecf3");
      root.style.setProperty("--swk-theme-icon", "#cfd8e3");
    } else {
      root.style.setProperty("--swk-bg", "#f8fbfd");
      root.style.setProperty("--swk-card", "#ffffff");
      root.style.setProperty("--swk-border", "#dde4ec");
      root.style.setProperty("--swk-muted", "#69727d");
      root.style.setProperty("--swk-input-bg", "#ffffff");
      root.style.setProperty("--swk-input-text", "#111111");
      root.style.setProperty("--swk-theme-icon", "#f5b700");
    }
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("swk_lang", preferredLanguage);
    localStorage.setItem("swk_currency", preferredCurrency);
  }, [preferredLanguage, preferredCurrency]);

  useEffect(() => {
    localStorage.setItem("swk_display_name", displayName || "");
  }, [displayName]);

  useEffect(() => {
    if (url.pathname === "/register") setInviteToken(tokenFromUrl);
    if (url.pathname === "/join-group") setJoinToken(tokenFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenFromUrl, url.pathname]);

  useEffect(() => {
    // Sync tab from path changes
    if (url.pathname.startsWith("/friends")) setCurrentTab("friends");
    else if (url.pathname.startsWith("/activity")) setCurrentTab("activity");
    else if (url.pathname.startsWith("/account") || url.pathname === "/profile") setCurrentTab("account");
    else if (url.pathname.startsWith("/groups")) setCurrentTab("groups");
    else setCurrentTab("friends");
    setShowGroupForm(false);
    setShowFriendForm(false);
    setGroupSearch("");
    setFriendSearch("");
    setOpenGroupSwipe(null);
    setOpenContactSwipe(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  async function loadMe() {
    const data = await me();
    setMeId(data.id);
    setMeEmail(data.email);
    setIsAdmin(data.is_admin);
    if (data.preferred_language) {
      setPreferredLanguage(data.preferred_language);
      localStorage.setItem("swk_lang", data.preferred_language);
    }
    if (data.preferred_currency) {
      setPreferredCurrency(data.preferred_currency);
      localStorage.setItem("swk_currency", data.preferred_currency);
    }
    setDisplayName(data.display_name ?? "");
    if (data.display_name !== undefined && data.display_name !== null) {
      localStorage.setItem("swk_display_name", data.display_name);
    }
    setMeData(data);
  }

  useEffect(() => {
    if (authToken) {
      loadMe().catch(() => {
        clearToken();
        setAuthToken(null);
        setMeId(null);
        setMeEmail(null);
        setIsAdmin(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => {
    if (route === "dashboard" && authToken) {
      Promise.all([refreshGroups(), refreshFriends(), refreshContacts()])
        .then(([gs, fs, cs]) => {
          if (currentTab === "activity") loadActivity(gs, fs, cs).catch(() => undefined);
        })
        .catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, authToken, currentTab]);

  useEffect(() => {
    if (route !== "dashboard" || !authToken) return;
    const onVisible = () => {
      if (document.hidden) return;
      if (currentTab === "groups") {
        refreshGroups().catch(() => undefined);
        refreshContacts().catch(() => undefined);
      } else if (currentTab === "friends") {
        refreshFriends().catch(() => undefined);
        refreshContacts().catch(() => undefined);
      } else if (currentTab === "activity") {
        Promise.all([refreshGroups(), refreshFriends(), refreshContacts()])
          .then(([gs, fs, cs]) => loadActivity(gs, fs, cs))
          .catch(() => undefined);
      } else if (currentTab === "account") {
        loadMe().catch(() => undefined);
      }
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, authToken, currentTab]);

  useEffect(() => {
    if (!meId) return;
    if (!groups && !contacts) return;
    const ids = activeBalanceGroupIds;
    setBalanceByGroup((prev) => {
      const next: typeof prev = {};
      ids.forEach((id) => {
        if (prev[id]) next[id] = prev[id];
      });
      return next;
    });
    balanceFetchGroupIds.forEach((id) => loadGroupBalance(id).catch(() => undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBalanceGroupIds, balanceFetchGroupIds, groups, contacts, meId]);

  useEffect(() => {
    if (currentTab === "activity") loadActivity().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, groups, friends, contacts]);

  useEffect(() => {
    if (!contacts) return;
    const next: Record<string, boolean> = { ...newGroupMembers };
    let changed = false;
    for (const c of contacts) {
      const cid = c.contact_user_id || "";
      if (!cid) continue;
      if (next[cid] === undefined) {
        next[cid] = false;
        changed = true;
      }
    }
    if (changed) setNewGroupMembers(next);
  }, [contacts, newGroupMembers]);

  async function onLogin(e: React.FormEvent) {
    e.preventDefault();
    setStatus(null);
    try {
      const token = await login(email, password);
      setToken(token.access_token);
      setAuthToken(token.access_token);
      await loadMe();
      navigate("/friends");
    } catch (err) {
      setStatus(String(err));
    }
  }

  async function onRegister(e: React.FormEvent) {
    e.preventDefault();
    setStatus(null);
    try {
      const token = await register(email, password, inviteToken);
      setToken(token.access_token);
      setAuthToken(token.access_token);
      await loadMe();
      navigate("/friends");
    } catch (err) {
      setStatus(String(err));
    }
  }

  async function onJoinGroup(e: React.FormEvent) {
    e.preventDefault();
    setStatus(null);
    try {
      await joinGroup(joinToken);
      await refreshGroups();
      navigate("/groups");
    } catch (err) {
      setStatus(String(err));
    }
  }

  function onLogout() {
    clearToken();
    setAuthToken(null);
    setMeId(null);
    setMeEmail(null);
    setIsAdmin(false);
    setMeData(null);
    setBalanceByGroup({});
    navigate("/");
  }

  async function refreshGroups() {
    const data = await listGroups();
    setGroups(data);
    return data;
  }

  async function refreshFriends() {
    const data = await listFriends();
    setFriends(data);
    return data;
  }

  async function loadGroupBalance(groupId: string) {
    if (!meId) return;
    if (balanceByGroup[groupId]) return;
    try {
      const bal = await getGroupBalances(groupId);
      const mine = bal.balances.find((b) => b.user_id === meId)?.net_minor ?? 0;
      setBalanceByGroup((prev) => ({ ...prev, [groupId]: { net: mine, currency: bal.currency_code } }));
    } catch {
      // ignore
    }
  }

  function apiErrorDetail(err: unknown): string | null {
    const msg = err instanceof Error ? err.message : String(err);
    const start = msg.indexOf("{");
    const end = msg.lastIndexOf("}");
    if (start < 0 || end <= start) return null;
    try {
      const parsed = JSON.parse(msg.slice(start, end + 1)) as { detail?: string };
      return typeof parsed.detail === "string" ? parsed.detail : null;
    } catch {
      return null;
    }
  }

  function startExpenseFlow(groupId: string) {
    navigate(`/groups/${groupId}`);
    setGroupExpenseTrigger((v) => v + 1);
  }

  function handleAddExpenseClick() {
    setExpensePickerTab(currentTab === "groups" ? "groups" : "friends");
    setExpensePickerOpen(true);
  }

  async function onLeaveGroup(groupId: string) {
    let knownNet: number | undefined = balanceByGroup[groupId]?.net;
    if (knownNet === undefined && meId) {
      try {
        const bal = await getGroupBalances(groupId);
        const mine = bal.balances.find((b) => b.user_id === meId)?.net_minor ?? 0;
        setBalanceByGroup((prev) => ({ ...prev, [groupId]: { net: mine, currency: bal.currency_code } }));
        knownNet = mine;
      } catch {
        // ignore
      }
    }
    if (typeof knownNet === "number" && knownNet !== 0) {
      alert(t(lang, "cannotLeaveGroupBalance"));
      return;
    }
    if (!confirm(t(lang, "leaveGroupConfirm"))) return;
    try {
      await leaveGroup(groupId);
      await refreshGroups();
      navigate("/groups");
    } catch (err) {
      const detail = apiErrorDetail(err);
      if (detail === "Cannot leave group with non-zero balance") {
        alert(t(lang, "cannotLeaveGroupBalance"));
      } else {
        alert(String(err));
      }
    }
  }

  async function loadActivity(groupList?: GroupResponse[], friendList?: FriendResponse[], contactList?: ContactResponse[]) {
    const activeGroups = groupList ?? groups ?? [];
    const activeFriends = friendList ?? friends ?? [];
    const activeContacts = contactList ?? contacts ?? [];
    setActivityLoading(true);
    try {
      const sources = new Map<string, { id: string; name: string }>();

      // 1) Real groups
      activeGroups.forEach((g) => sources.set(g.id, { id: g.id, name: g.name ?? "" }));

      // 2) Friendship ledgers (friends list)
      activeFriends.forEach((f) => {
        const name = f.friend_display_name || f.friend_email || "Friend";
        if (!sources.has(f.group_id)) sources.set(f.group_id, { id: f.group_id, name });
      });

      // 3) Contact ledgers (contacts list)
      activeContacts.forEach((c) => {
        if (!c.group_id) return;
        const name = c.resolved_display_name || c.email || c.phone || "Contact";
        if (!sources.has(c.group_id)) sources.set(c.group_id, { id: c.group_id, name });
      });

      const perSource = await Promise.allSettled(
        [...sources.values()].map(async (src) => {
          const expenses = await listGroupExpenses(src.id, false);
          return expenses.map((e) => ({ ...e, group_name: src.name }));
        }),
      );

      const flat = perSource
        .flatMap((r) => (r.status === "fulfilled" ? r.value : []))
        .sort((a, b) => (a.occurred_at < b.occurred_at ? 1 : -1))
        .slice(0, 30);
      setActivityItems(flat);
    } catch (err) {
      setStatus(String(err));
    } finally {
      setActivityLoading(false);
    }
  }

  async function refreshContacts() {
    const data = await listContacts();
    setContacts(data);
    if (newGroupMembers && Object.keys(newGroupMembers).length === 0) {
      const defaults: Record<string, boolean> = {};
      for (const c of data) {
        if (c.contact_user_id) defaults[c.contact_user_id] = false;
      }
      setNewGroupMembers(defaults);
    }
    return data;
  }

  async function onCreateInvite() {
    setStatus(null);
    setInviteCopied(false);
    try {
      const inv = await createInvite();
      setInviteResult(inv);
    } catch (err) {
      setStatus(String(err));
    }
  }

  async function onCreateGroup(e: React.FormEvent) {
    e.preventDefault();
    setStatus(null);
    try {
      const member_ids = Object.entries(newGroupMembers)
        .filter(([, v]) => v)
        .map(([k]) => k);
      const g = await createGroup({
        name: newGroupName,
        currency_code: newGroupCurrency,
        description: newGroupDescription || null,
        avatar_attachment_id: newGroupAvatarId,
        member_ids,
      });
      setNewGroupDescription("");
      setNewGroupAvatarId(null);
      setNewGroupMembers({});
      setShowGroupForm(false);
      refreshGroups().catch(() => undefined);
      refreshContacts().catch(() => undefined);
      const target = `/groups/${g.id}`;
      navigate(target);
      window.location.assign(target);
      return;
    } catch (err) {
      setStatus(String(err));
    }
  }

  async function onAddFriend(e: React.FormEvent) {
    e.preventDefault();
    setStatus(null);
    try {
      const email = newContactEmail.trim().toLowerCase();
      const phone = newContactPhone.trim();
      const local_display_name = newContactLocalName.trim();
      if (!email && !phone) throw new Error("Enter email or phone");
      await createContact({
        email: email || null,
        phone: phone || null,
        local_display_name: local_display_name || null,
      });
      setNewContactEmail("");
      setNewContactPhone("");
      setNewContactLocalName("");
      setShowFriendForm(false);
      await refreshContacts();
    } catch (err) {
      const msg = String(err);
      if (msg.includes("Contact already exists")) {
        alert(t(lang, "contactExists"));
      }
      setStatus(msg);
    }
  }

  async function onSelectGroupAvatar(file: File) {
    setStatus(null);
    const upload = await uploadFile(file);
    setNewGroupAvatarId(upload.id);
  }

  function openContactEdit(c: ContactResponse) {
    setContactEditModal({
      id: c.id,
      name: c.local_display_name || c.resolved_display_name,
      phone: c.phone || "",
      email: c.email || "",
    });
  }

  async function onDeleteContact(c: ContactResponse) {
    if (!confirm(t(lang, "delete"))) return;
    setOpenContactSwipe(null);
    setContactSaving((s) => ({ ...s, [c.id]: true }));
    try {
      await deleteContact(c.id);
      await refreshContacts();
    } catch (err) {
      const detail = apiErrorDetail(err);
      if (detail === "Cannot delete contact with non-zero balance") {
        alert(t(lang, "cannotDeleteContactBalance"));
        return;
      }
      alert(String(err));
    } finally {
      setContactSaving((s) => ({ ...s, [c.id]: false }));
    }
  }

  return (
    <div
      style={{
        maxWidth: 960,
        margin: "0 auto",
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto",
        padding: "0 12px 90px",
        background: theme === "dark" ? "#0f141a" : "#f8fbfd",
        minHeight: "100vh",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>{!authToken ? <h1 style={{ margin: "12px 0", fontSize: 24, letterSpacing: -0.5 }}>{t(lang, "title")}</h1> : null}</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div className="swk-header-controls">
            <div className="swk-lang-toggle">
              <button
                type="button"
                className="swk-lang-link"
                data-testid="lang-ru"
                aria-pressed={preferredLanguage === "ru"}
                style={{
                  opacity: preferredLanguage === "ru" ? 1 : 0.55,
                  textDecoration: preferredLanguage === "ru" ? "underline" : "none",
                }}
                onClick={() => {
                  setPreferredLanguage("ru");
                  localStorage.setItem("swk_lang", "ru");
                }}
              >
                RU
              </button>
              <span className="swk-lang-sep">/</span>
              <button
                type="button"
                className="swk-lang-link"
                data-testid="lang-en"
                aria-pressed={preferredLanguage === "en"}
                style={{
                  opacity: preferredLanguage === "en" ? 1 : 0.55,
                  textDecoration: preferredLanguage === "en" ? "underline" : "none",
                }}
                onClick={() => {
                  setPreferredLanguage("en");
                  localStorage.setItem("swk_lang", "en");
                }}
              >
                EN
              </button>
            </div>
            <button
              type="button"
              className="swk-theme-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label={`${t(lang, "theme")}: ${t(lang, theme === "dark" ? "dark" : "light")}`}
              title={`${t(lang, "theme")}: ${t(lang, theme === "dark" ? "dark" : "light")}`}
            >
              {theme === "dark" ? (
                <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M21 13.3A8.3 8.3 0 0 1 10.7 3a7.5 7.5 0 1 0 10.3 10.3Z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
                  <path
                    d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </button>
          </div>
          {meEmail && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
              <div style={{ textAlign: "right" }} className="swk-user-panel">
                <div style={{ fontWeight: 600 }} data-testid="user-display" className="swk-ellipsis">
                  {meData?.display_name || displayName || meEmail}
                </div>
                <div style={{ fontSize: 12, color: theme === "dark" ? "#9fb3d1" : "#666" }} className="swk-ellipsis">
                  {preferredLanguage} / {preferredCurrency}
                </div>
                {Object.keys(overallByCurrency).length > 0 && (
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    {Object.entries(overallByCurrency).map(([cur, net]) => (
                      <span
                        key={cur}
                        style={{
                          fontSize: 12,
                          color: net < 0 ? "#c65a2b" : net > 0 ? "#0f8c57" : theme === "dark" ? "#9fb3d1" : "#666",
                          fontWeight: 700,
                        }}
                        className="swk-ellipsis"
                      >
                        {formatMoney(net, cur)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button
                data-testid="user-avatar"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  border: theme === "dark" ? "1px solid #273244" : "1px solid #dde4ec",
                  overflow: "hidden",
                  padding: 0,
                  background: theme === "dark" ? "#0e1116" : "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme === "dark" ? "#e7ecf3" : "#111",
                }}
                onClick={() => setMenuOpen((v) => !v)}
              >
                {meData?.avatar_url ? (
                  <AuthedImage
                    src={meData.avatar_url}
                    alt="avatar"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    fallback={(displayName || meEmail || "?").slice(0, 1).toUpperCase()}
                  />
                ) : (
                  (displayName || meEmail || "?").slice(0, 1).toUpperCase()
                )}
              </button>
              {menuOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "120%",
                    right: 0,
                    background: cardBg,
                    border: theme === "dark" ? "1px solid #273244" : "1px solid #dde4ec",
                    borderRadius: 8,
                    boxShadow: cardShadow,
                    minWidth: 160,
                    zIndex: 10,
                    padding: 8,
                  }}
                >
                  <button
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "10px 10px",
                      borderRadius: 10,
                      border: "none",
                      background: "transparent",
                      color: theme === "dark" ? "#e7ecf3" : "#111",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/profile")}
                  >
                    {t(lang, "profile")}
                  </button>
                  <button
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "10px 10px",
                      borderRadius: 10,
                      border: "none",
                      background: "transparent",
                      color: theme === "dark" ? "#e7ecf3" : "#111",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                    onClick={onLogout}
                  >
                    {t(lang, "logout")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {route === "group" && groupIdFromPath && (
        <GroupPage
          groupId={groupIdFromPath}
          meId={meId}
          lang={lang}
          currencyOptions={currencyOptions}
          triggerNewExpense={groupExpenseTrigger}
          onBalanceUpdated={(gid, currency, netMinor) =>
            setBalanceByGroup((prev) => ({
              ...prev,
              [gid]: { net: netMinor, currency },
            }))
          }
          onNavigate={(p) => {
            navigate(p);
          }}
        />
      )}

      {route === "profile" && (
        <ProfilePage
          me={meData}
          lang={lang}
          currencyOptions={currencyOptions}
          languageOptions={languageOptions}
          onProfileUpdated={(resp) => {
            setMeData(resp);
            if (resp.preferred_language) {
              setPreferredLanguage(resp.preferred_language);
              localStorage.setItem("swk_lang", resp.preferred_language);
            }
            if (resp.preferred_currency) {
              setPreferredCurrency(resp.preferred_currency);
              localStorage.setItem("swk_currency", resp.preferred_currency);
            }
            if (resp.display_name !== undefined) setDisplayName(resp.display_name || "");
            loadMe().catch(() => undefined);
          }}
          onDisplayNameChange={(name) => setDisplayName(name)}
          onNavigate={navigate}
        />
      )}

      {route === "support" && <SupportPage theme={theme} cardStyle={cardStyle} onNavigate={navigate} />}

      {route === "privacy" && <PrivacyPage theme={theme} cardStyle={cardStyle} onNavigate={navigate} />}

      {route === "dashboard" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 32 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              {t(
                lang,
                currentTab === "activity"
                  ? "recentActivityTitle"
                  : currentTab === "account"
                    ? "accountSettings"
                    : isOverallSettledUp
                      ? "youAreAllSettledUp"
                      : currentTab === "groups"
                        ? "groups"
                        : "friends",
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onLogout} style={{ border: "none", background: "transparent", color: "#0f8c57", fontWeight: 600 }}>
                {t(lang, "logout")}
              </button>
            </div>
          </div>

          {currentTab === "groups" && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ color: "#0f8c57", fontWeight: 700 }}>{t(lang, "groups")}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => setShowGroupForm((v) => !v)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 12,
                      border: "1px solid #0f8c57",
                      background: "#0f8c57",
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  >
                    {showGroupForm ? t(lang, "cancel") : `+ ${t(lang, "createGroup")}`}
                  </button>
                </div>
              </div>
              <input
                className="swk-input"
                style={{ ...inputStyle, marginTop: 10 }}
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                placeholder={t(lang, "search")}
              />
              {showGroupForm && (
                <form
                  onSubmit={onCreateGroup}
                  style={{
                    display: "grid",
                    gap: 10,
                    padding: 12,
                    background: "#ffffff",
                    borderRadius: 16,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  }}
                >
                  <label style={{ display: "grid", gap: 4 }}>
                    <span style={{ fontWeight: 600 }}>{t(lang, "name")}</span>
                    <input
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #dde4ec" }}
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span style={{ fontWeight: 600 }}>{t(lang, "description")}</span>
                    <textarea
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #dde4ec" }}
                      value={newGroupDescription}
                      onChange={(e) => setNewGroupDescription(e.target.value)}
                      rows={3}
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span style={{ fontWeight: 600 }}>{t(lang, "currency")}</span>
                    <select
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #dde4ec" }}
                      value={newGroupCurrency}
                      onChange={(e) => setNewGroupCurrency(e.target.value)}
                    >
                      {currencyOptions.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label style={{ display: "grid", gap: 6 }}>
                    <span style={{ fontWeight: 600 }}>{t(lang, "avatar")}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          onSelectGroupAvatar(file).catch((err) => setStatus(String(err)));
                        }
                      }}
                    />
                    {newGroupAvatarId ? <div style={{ fontSize: 12, color: "#555" }}>{t(lang, "avatarReady")}</div> : null}
                  </label>
                  <div>
                    <div style={{ marginBottom: 6, fontWeight: 600 }}>{t(lang, "addMembers")}</div>
                    {contacts && contacts.length > 0 ? (
                      <div style={{ display: "grid", gap: 6 }}>
                        {contacts.map((c) => (
                          <label key={c.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <input
                              type="checkbox"
                              disabled={!c.contact_user_id}
                              checked={Boolean(c.contact_user_id && newGroupMembers[c.contact_user_id])}
                              onChange={(e) =>
                                c.contact_user_id
                                  ? setNewGroupMembers({ ...newGroupMembers, [c.contact_user_id]: e.target.checked })
                                  : undefined
                              }
                            />
                            <span>
                              {c.resolved_display_name}
                              {isPendingContact(c) ? ` · ${t(lang, "pendingRegistration")}` : ""}
                            </span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: "#666" }}>{t(lang, "noContacts")}</div>
                    )}
                  </div>
                  <button
                    type="submit"
                    style={{
                      padding: "12px",
                      background: "#0f8c57",
                      color: "#fff",
                      borderRadius: 12,
                      border: "none",
                      fontWeight: 700,
                    }}
                  >
                    {t(lang, "createGroup")}
                  </button>
                </form>
              )}

              {groups && filteredGroups.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {filteredGroups.map((g) => (
                    <div key={g.id}>
                      {isMobileTouch ? (
                        <SwipeRow
                          id={g.id}
                          testId={`swipe-group-${g.id}`}
                          openSide={openGroupSwipe?.id === g.id ? openGroupSwipe.side : null}
                          onOpenSideChange={(id, side) => setOpenGroupSwipe(side ? { id, side } : null)}
                          leftActions={
                            g.kind === "group" ? (
                              <button
                                type="button"
                                data-testid={`group-edit-${g.id}`}
                                style={{
                                  border: "1px solid var(--swk-border)",
                                  background: "var(--swk-input-bg)",
                                  color: "#0f8c57",
                                  borderRadius: 10,
                                  padding: "6px 10px",
                                  fontWeight: 700,
                                }}
                                onClick={() => {
                                  setOpenGroupSwipe(null);
                                  navigate(`/groups/${g.id}?edit=1`);
                                }}
                              >
                                {t(lang, "edit")}
                              </button>
                            ) : undefined
                          }
                          rightActions={
                            <button
                              type="button"
                              data-testid={`group-leave-${g.id}`}
                              style={{
                                border: "1px solid #f2d2c2",
                                background: "var(--swk-input-bg)",
                                color: "#c65a2b",
                                borderRadius: 10,
                                padding: "6px 10px",
                                fontWeight: 700,
                              }}
                              onClick={() => {
                                setOpenGroupSwipe(null);
                                onLeaveGroup(g.id).catch(() => undefined);
                              }}
                            >
                              {t(lang, "leaveGroup")}
                            </button>
                          }
                        >
                          <div
                            data-testid={`group-card-${g.id}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              cursor: "pointer",
                            }}
                            className="swk-group-card"
                            onClick={() => navigate(`/groups/${g.id}`)}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <div
                                style={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 16,
                                  background: g.avatar_url ? "transparent" : "#e8f3ec",
                                  overflow: "hidden",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: 700,
                                  color: "#0f8c57",
                                }}
                              >
                                {g.avatar_url ? (
                                  <AuthedImage
                                    src={g.avatar_url}
                                    alt="avatar"
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    fallback={(g.name || "G").slice(0, 1).toUpperCase()}
                                  />
                                ) : (
                                  (g.name || "G").slice(0, 1).toUpperCase()
                                )}
                              </div>
                              <div>
                                <div style={{ fontWeight: 700 }}>{g.name}</div>
                                {g.description ? <div style={{ fontSize: 12, color: "var(--swk-muted)" }}>{g.description}</div> : null}
                                {typeof g.member_count === "number" ? (
                                  <div style={{ fontSize: 12, color: "var(--swk-muted)" }}>
                                    {g.member_count} {lang === "ru" ? "участников" : "members"}
                                  </div>
                                ) : null}
                                <div style={{ fontSize: 12, color: "#0f8c57", fontWeight: 600 }}>{g.currency_code}</div>
                                {balanceByGroup[g.id] && (
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color:
                                        balanceByGroup[g.id].net < 0
                                          ? "#c65a2b"
                                          : balanceByGroup[g.id].net > 0
                                            ? "#0f8c57"
                                            : "#69727d",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {formatMoney(balanceByGroup[g.id].net, balanceByGroup[g.id].currency)}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                              <div style={{ fontSize: 12, color: "#69727d" }}>
                                {balanceByGroup[g.id]
                                  ? balanceByGroup[g.id].net === 0
                                    ? t(lang, "settledUp")
                                    : balanceByGroup[g.id].net < 0
                                      ? t(lang, "youOwe")
                                      : t(lang, "youAreOwed")
                                  : t(lang, "settledUp")}
                              </div>
                            </div>
                          </div>
                        </SwipeRow>
                      ) : (
                        <div
                          data-testid={`group-card-${g.id}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "10px 12px",
                            background: "var(--swk-card)",
                            color: "var(--swk-input-text)",
                            borderRadius: 14,
                            boxShadow: "0 8px 18px rgba(0,0,0,0.04)",
                            cursor: "pointer",
                          }}
                          className="swk-group-card"
                          onClick={() => navigate(`/groups/${g.id}`)}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div
                              style={{
                                width: 48,
                                height: 48,
                                borderRadius: 16,
                                background: g.avatar_url ? "transparent" : "#e8f3ec",
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                color: "#0f8c57",
                              }}
                            >
                              {g.avatar_url ? (
                                <AuthedImage
                                  src={g.avatar_url}
                                  alt="avatar"
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  fallback={(g.name || "G").slice(0, 1).toUpperCase()}
                                />
                              ) : (
                                (g.name || "G").slice(0, 1).toUpperCase()
                              )}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700 }}>{g.name}</div>
                              {g.description ? <div style={{ fontSize: 12, color: "var(--swk-muted)" }}>{g.description}</div> : null}
                              {typeof g.member_count === "number" ? (
                                <div style={{ fontSize: 12, color: "var(--swk-muted)" }}>
                                  {g.member_count} {lang === "ru" ? "участников" : "members"}
                                </div>
                              ) : null}
                              <div style={{ fontSize: 12, color: "#0f8c57", fontWeight: 600 }}>{g.currency_code}</div>
                              {balanceByGroup[g.id] && (
                                <div
                                  style={{
                                    fontSize: 12,
                                    color:
                                      balanceByGroup[g.id].net < 0
                                        ? "#c65a2b"
                                        : balanceByGroup[g.id].net > 0
                                          ? "#0f8c57"
                                          : "#69727d",
                                    fontWeight: 700,
                                  }}
                                >
                                  {formatMoney(balanceByGroup[g.id].net, balanceByGroup[g.id].currency)}
                                </div>
                              )}
                            </div>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                            <div style={{ fontSize: 12, color: "#69727d" }}>
                              {balanceByGroup[g.id]
                                ? balanceByGroup[g.id].net === 0
                                  ? t(lang, "settledUp")
                                  : balanceByGroup[g.id].net < 0
                                    ? t(lang, "youOwe")
                                    : t(lang, "youAreOwed")
                                : t(lang, "settledUp")}
                            </div>
                            <button
                              type="button"
                              data-testid={`group-leave-inline-${g.id}`}
                              title={t(lang, "leaveGroup")}
                              aria-label={t(lang, "leaveGroup")}
                              onClick={(e) => {
                                e.stopPropagation();
                                onLeaveGroup(g.id).catch(() => undefined);
                              }}
                              style={{
                                border: "none",
                                background: "transparent",
                                color: "#c65a2b",
                                fontSize: 18,
                                cursor: balanceByGroup[g.id] && balanceByGroup[g.id].net !== 0 ? "not-allowed" : "pointer",
                                opacity: balanceByGroup[g.id] && balanceByGroup[g.id].net !== 0 ? 0.35 : 1,
                              }}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "#69727d", padding: "8px 0" }}>{t(lang, "none")}</div>
              )}
            </>
          )}

          {currentTab === "friends" && (
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ color: "#0f8c57", fontWeight: 700 }}>{t(lang, "friends")}</div>
                <button
                  type="button"
                  onClick={() => setShowFriendForm((v) => !v)}
                  style={{
                    padding: "10px 14px",
                    background: showFriendForm ? "#0f8c57" : "#fff",
                    color: showFriendForm ? "#fff" : "#0f8c57",
                    borderRadius: 10,
                    border: "1px solid #0f8c57",
                    fontWeight: 700,
                  }}
                >
                  {showFriendForm ? t(lang, "cancel") : t(lang, "addFriend")}
                </button>
              </div>
              <input
                className="swk-input"
                style={{ ...inputStyle }}
                value={friendSearch}
                onChange={(e) => setFriendSearch(e.target.value)}
                placeholder={t(lang, "search")}
              />
              {showFriendForm && (
                <form onSubmit={onAddFriend} style={{ display: "grid", gap: 8, padding: 10, background: "#fff", borderRadius: 12, boxShadow: "0 6px 14px rgba(0,0,0,0.05)" }}>
                  <input
                    style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #dde4ec" }}
                    placeholder={t(lang, "friendEmail")}
                    value={newContactEmail}
                    onChange={(e) => setNewContactEmail(e.target.value)}
                  />
                  <input
                    style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #dde4ec" }}
                    placeholder={t(lang, "phone")}
                    value={newContactPhone}
                    onChange={(e) => setNewContactPhone(e.target.value)}
                  />
                  <input
                    style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #dde4ec" }}
                    placeholder={t(lang, "localDisplayName")}
                    value={newContactLocalName}
                    onChange={(e) => setNewContactLocalName(e.target.value)}
                  />
                  <button
                    type="submit"
                    style={{ padding: "12px", background: "#0f8c57", color: "#fff", borderRadius: 10, border: "none", fontWeight: 700 }}
                  >
                    {t(lang, "addFriend")}
                  </button>
                  <div style={{ fontSize: 12, color: "#69727d" }}>{t(lang, "contactHint")}</div>
                </form>
              )}
              <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                {contacts && filteredContacts.length > 0 ? (
                  <div style={{ display: "grid", gap: 10 }}>
                    {filteredContacts.map((c) => (
                      <div key={c.id}>
                        {isMobileTouch ? (
                          <SwipeRow
                            id={c.id}
                            testId={`swipe-contact-${c.id}`}
                            openSide={openContactSwipe?.id === c.id ? openContactSwipe.side : null}
                            onOpenSideChange={(id, side) => setOpenContactSwipe(side ? { id, side } : null)}
                            leftActions={
                              <button
                                type="button"
                                data-testid={`contact-edit-${c.id}`}
                                style={{
                                  border: "1px solid #0f8c57",
                                  background: "#fff",
                                  color: "#0f8c57",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  fontWeight: 700,
                                }}
                                onClick={() => {
                                  setOpenContactSwipe(null);
                                  openContactEdit(c);
                                }}
                              >
                                {t(lang, "edit")}
                              </button>
                            }
                            rightActions={
                              <button
                                type="button"
                                data-testid={`contact-delete-${c.id}`}
                                style={{
                                  border: "1px solid #f2d2c2",
                                  background: "var(--swk-input-bg)",
                                  color: "#c65a2b",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  fontWeight: 700,
                                  opacity: contactSaving[c.id] ? 0.5 : 1,
                                }}
                                onClick={() => onDeleteContact(c).catch(() => undefined)}
                                disabled={contactSaving[c.id]}
                              >
                                {t(lang, "delete")}
                              </button>
                            }
                          >
                            <div
                              data-testid={`contact-card-${c.id}`}
                              style={{
                                display: "grid",
                                gap: 10,
                                color: "var(--swk-input-text)",
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <button
                                  type="button"
                                  disabled={!c.group_id}
                                  onClick={() => c.group_id && navigate(`/groups/${c.group_id}`)}
                                  title={t(lang, "openLedger")}
                                  aria-label={t(lang, "openLedger")}
                                  style={{
                                    width: 46,
                                    height: 46,
                                    borderRadius: 16,
                                    background: "#e8f3ec",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 800,
                                    color: "#0f8c57",
                                    fontSize: 18,
                                    border: "none",
                                    padding: 0,
                                    cursor: c.group_id ? "pointer" : "not-allowed",
                                    opacity: c.group_id ? 1 : 0.6,
                                    overflow: "hidden",
                                  }}
                                >
                                  {c.avatar_url ? (
                                    <AuthedImage
                                      src={c.avatar_url}
                                      alt={c.resolved_display_name || c.email || "Contact"}
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                      fallback={<span>{(c.resolved_display_name || c.email || "?").slice(0, 1).toUpperCase()}</span>}
                                    />
                                  ) : (
                                    (c.resolved_display_name || c.email || "?").slice(0, 1).toUpperCase()
                                  )}
                                </button>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <div style={{ fontWeight: 700, fontSize: 15 }}>{c.resolved_display_name}</div>
                                    {isRegisteredContact(c) ? (
                                      <span
                                        title="Registered"
                                        aria-label="Registered"
                                        style={{ color: "#2563eb", fontSize: 14, lineHeight: 1 }}
                                      >
                                        ✓
                                      </span>
                                    ) : null}
                                  </div>
                                  <div style={{ fontSize: 12, color: "var(--swk-muted)" }}>
                                    {c.email || ""}
                                    {isPendingContact(c) ? ` · ${t(lang, "pendingRegistration")}` : ""}
                                  </div>
                                  {c.group_id && balanceByGroup[c.group_id] && (
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color:
                                          balanceByGroup[c.group_id].net < 0
                                            ? "#c65a2b"
                                            : balanceByGroup[c.group_id].net > 0
                                              ? "#0f8c57"
                                              : "#69727d",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {formatMoney(balanceByGroup[c.group_id].net, balanceByGroup[c.group_id].currency)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </SwipeRow>
                        ) : (
                          <div
                            data-testid={`contact-card-${c.id}`}
                            style={{
                              display: "grid",
                              gap: 10,
                              padding: "12px",
                              background: "var(--swk-card)",
                              color: "var(--swk-input-text)",
                              borderRadius: 14,
                              boxShadow: "0 8px 18px rgba(0,0,0,0.04)",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <button
                                type="button"
                                disabled={!c.group_id}
                                onClick={() => c.group_id && navigate(`/groups/${c.group_id}`)}
                                title={t(lang, "openLedger")}
                                aria-label={t(lang, "openLedger")}
                                style={{
                                  width: 46,
                                  height: 46,
                                  borderRadius: 16,
                                  background: "#e8f3ec",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: 800,
                                  color: "#0f8c57",
                                  fontSize: 18,
                                  border: "none",
                                  padding: 0,
                                  cursor: c.group_id ? "pointer" : "not-allowed",
                                  opacity: c.group_id ? 1 : 0.6,
                                  overflow: "hidden",
                                }}
                              >
                                {c.avatar_url ? (
                                  <AuthedImage
                                    src={c.avatar_url}
                                    alt={c.resolved_display_name || c.email || "Contact"}
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    fallback={<span>{(c.resolved_display_name || c.email || "?").slice(0, 1).toUpperCase()}</span>}
                                  />
                                ) : (
                                  (c.resolved_display_name || c.email || "?").slice(0, 1).toUpperCase()
                                )}
                              </button>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <div style={{ fontWeight: 700, fontSize: 15 }}>{c.resolved_display_name}</div>
                                  {isRegisteredContact(c) ? (
                                    <span
                                      title="Registered"
                                      aria-label="Registered"
                                      style={{ color: "#2563eb", fontSize: 14, lineHeight: 1 }}
                                    >
                                      ✓
                                    </span>
                                  ) : null}
                                </div>
                                <div style={{ fontSize: 12, color: "var(--swk-muted)" }}>
                                  {c.email || ""}
                                  {isPendingContact(c) ? ` · ${t(lang, "pendingRegistration")}` : ""}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => openContactEdit(c)}
                                  style={{
                                    border: "1px solid #0f8c57",
                                    background: "#fff",
                                    color: "#0f8c57",
                                    borderRadius: 8,
                                    padding: "4px 8px",
                                    fontWeight: 700,
                                    marginTop: 6,
                                  }}
                                >
                                  {t(lang, "edit")}
                                </button>
                                {c.group_id && balanceByGroup[c.group_id] && (
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color:
                                        balanceByGroup[c.group_id].net < 0
                                          ? "#c65a2b"
                                          : balanceByGroup[c.group_id].net > 0
                                            ? "#0f8c57"
                                            : "#69727d",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {formatMoney(balanceByGroup[c.group_id].net, balanceByGroup[c.group_id].currency)}
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                data-testid={`contact-delete-${c.id}`}
                                title={t(lang, "delete")}
                                aria-label={t(lang, "delete")}
                                onClick={() => onDeleteContact(c).catch(() => undefined)}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: "#c65a2b",
                                  fontSize: 18,
                                  cursor: "pointer",
                                  opacity: contactSaving[c.id] ? 0.5 : 1,
                                }}
                                disabled={contactSaving[c.id]}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: "#69727d" }}>{t(lang, "none")}</div>
                )}
              </div>
            </div>
          )}

          {currentTab === "activity" && (
            <div style={{ display: "grid", gap: 10 }}>
              {activityLoading && <div style={{ color: "#69727d" }}>{t(lang, "loading")}</div>}
              {!activityLoading && activityItems.length === 0 && <div style={{ color: "#69727d" }}>{t(lang, "none")}</div>}
              {activityItems.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: "flex",
                    gap: 12,
                    padding: "10px 12px",
                    background: "#fff",
                    borderRadius: 14,
                    boxShadow: "0 8px 18px rgba(0,0,0,0.04)",
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 14,
                      background: "#e9f4ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      color: "#0f8c57",
                    }}
                  >
                    ¥
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>
                      {item.description} <span style={{ color: "#69727d" }}>· {item.group_name}</span>
                    </div>
                    <div style={{ color: "#d2551f", fontWeight: 700 }}>
                      {item.amount_minor / 100} {item.currency_code}
                    </div>
                    <div style={{ fontSize: 12, color: "#69727d" }}>{new Date(item.occurred_at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {currentTab === "account" && (
            <div style={{ display: "grid", gap: 14 }}>
              <ProfilePage
                me={meData}
                lang={lang}
                currencyOptions={currencyOptions}
                languageOptions={languageOptions}
                onProfileUpdated={(resp) => {
                  setMeData(resp);
                  if (resp.preferred_language) {
                    setPreferredLanguage(resp.preferred_language);
                    localStorage.setItem("swk_lang", resp.preferred_language);
                  }
                  if (resp.preferred_currency) {
                    setPreferredCurrency(resp.preferred_currency);
                    localStorage.setItem("swk_currency", resp.preferred_currency);
                  }
                  if (resp.display_name !== undefined) setDisplayName(resp.display_name || "");
                  loadMe().catch(() => undefined);
                }}
                onDisplayNameChange={(name) => setDisplayName(name)}
                onNavigate={navigate}
              />

              <div style={cardStyle}>
                <div style={{ fontWeight: 900, marginBottom: 8 }}>{t(lang, "createInvite")}</div>
                <div style={{ fontSize: 12, color: "#69727d", marginBottom: 10 }}>{t(lang, "inviteHelp")}</div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <button type="button" style={primaryButton} onClick={onCreateInvite} data-testid="create-invite">
                    {t(lang, "createInvite")}
                  </button>

                  {inviteResult?.url && (
                    <>
                      <button
                        type="button"
                        style={{ ...secondaryButton, width: "auto" }}
                        data-testid="copy-invite-url"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(inviteResult.url || "");
                            setInviteCopied(true);
                            window.setTimeout(() => setInviteCopied(false), 1500);
                          } catch {
                            // Fallback for environments where Clipboard API is unavailable.
                            window.prompt(t(lang, "copy"), inviteResult.url || "");
                          }
                        }}
                      >
                        {inviteCopied ? t(lang, "copied") : t(lang, "copy")}
                      </button>

                      <a
                        href={`mailto:?subject=${encodeURIComponent("SplitwiseKiller invite")}&body=${encodeURIComponent(inviteResult.url)}`}
                        style={{
                          ...secondaryButton,
                          width: "auto",
                          textDecoration: "none",
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                        data-testid="open-mail-invite"
                      >
                        {t(lang, "openMail")}
                      </a>
                    </>
                  )}
                </div>

                {inviteResult && (
                  <pre
                    data-testid="invite-result"
                    style={{ marginTop: 12, whiteSpace: "pre-wrap", background: "#f6f6f6", padding: 12, borderRadius: 12 }}
                  >
                    {t(lang, "inviteToken")}: {inviteResult.token}
                    {"\n"}
                    {t(lang, "inviteUrl")}: {inviteResult.url ?? "(null)"}
                  </pre>
                )}
              </div>

              <div style={cardStyle} data-testid="build-info">
                <div style={{ fontWeight: 900, marginBottom: 8 }}>{t(lang, "buildInfo")}</div>
                <div style={{ fontSize: 12, color: "#69727d", display: "grid", gap: 4 }}>
                  <div>
                    {t(lang, "buildVersion")}: {buildInfo.version}
                  </div>
                  <div>
                    {t(lang, "buildCommit")}: {buildInfo.commit}
                  </div>
                  <div>
                    {t(lang, "buildTime")}: {buildInfo.builtAt}
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      {authToken && route !== "login" && route !== "register" && route !== "join-group" && (
        <>
          {route === "dashboard" && (currentTab === "groups" || currentTab === "friends" || currentTab === "activity") && (
            <button className="swk-fab" onClick={handleAddExpenseClick} aria-label={t(lang, "addExpense")} title={t(lang, "addExpense")}>
              +
            </button>
          )}

          <div className="swk-bottom-nav">
            {([
              { key: "friends", icon: "👥" },
              { key: "groups", icon: "🧭" },
              { key: "activity", icon: "🗂️" },
              { key: "account", icon: "⚙️" },
            ] as const).map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  const path =
                    tab.key === "friends"
                      ? "/friends"
                      : tab.key === "activity"
                        ? "/activity"
                        : tab.key === "account"
                          ? "/account"
                          : "/groups";
                  navigate(path);
                }}
                className={`swk-tab-btn ${currentTab === tab.key ? "swk-tab-btn-active" : ""}`}
              >
                <span style={{ fontSize: 18 }}>{tab.icon}</span>
                <span>
              {tab.key === "friends"
                ? t(lang, "friends")
                : tab.key === "groups"
                  ? t(lang, "groups")
                  : tab.key === "activity"
                    ? t(lang, "activity")
                    : t(lang, "account")}
            </span>
          </button>
        ))}
      </div>
    </>
  )}

      {expensePickerOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 12,
            zIndex: 2500,
          }}
          onClick={() => setExpensePickerOpen(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 520,
              background: theme === "dark" ? "#121923" : "#fff",
              color: theme === "dark" ? "#e7ecf3" : "#111",
              borderRadius: 16,
              padding: 12,
              boxShadow: "0 18px 50px rgba(0,0,0,0.22)",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontWeight: 900, fontSize: 18 }}>{t(lang, "addExpense")}</div>
              <button
                type="button"
                onClick={() => setExpensePickerOpen(false)}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 18,
                  cursor: "pointer",
                  color: theme === "dark" ? "#e7ecf3" : "#111",
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
              <button
                type="button"
                onClick={() => setExpensePickerTab("friends")}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: expensePickerTab === "friends" ? "2px solid #0f8c57" : "1px solid #dde4ec",
                  background:
                    expensePickerTab === "friends"
                      ? "#e8f3ec"
                      : theme === "dark"
                        ? "#0e1116"
                        : "#fff",
                  fontWeight: 900,
                  color: "#0f8c57",
                  cursor: "pointer",
                }}
              >
                {t(lang, "friends")}
              </button>
              <button
                type="button"
                onClick={() => setExpensePickerTab("groups")}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: expensePickerTab === "groups" ? "2px solid #0f8c57" : "1px solid #dde4ec",
                  background:
                    expensePickerTab === "groups"
                      ? "#e8f3ec"
                      : theme === "dark"
                        ? "#0e1116"
                        : "#fff",
                  fontWeight: 900,
                  color: "#0f8c57",
                  cursor: "pointer",
                }}
              >
                {t(lang, "groups")}
              </button>
            </div>

            {expensePickerTab === "friends" ? (
              <div style={{ display: "grid", gap: 8 }}>
                {visibleContacts.filter((c) => Boolean(c.group_id)).length === 0 ? (
                  <div style={{ color: "#69727d" }}>{t(lang, "none")}</div>
                ) : (
                  visibleContacts
                    .filter((c) => Boolean(c.group_id))
                    .map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          if (!c.group_id) return;
                          setExpensePickerOpen(false);
                          startExpenseFlow(c.group_id);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "12px",
                          borderRadius: 14,
                          border: theme === "dark" ? "1px solid #273244" : "1px solid #dde4ec",
                          background: theme === "dark" ? "#0e1116" : "#fff",
                          color: theme === "dark" ? "#e7ecf3" : "#111",
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                      >
                        <div
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 16,
                            background: "#e8f3ec",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 900,
                            color: "#0f8c57",
                            flex: "0 0 auto",
                          }}
                        >
                          {(c.resolved_display_name || c.email || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {c.resolved_display_name}
                          </div>
                        </div>
                      </button>
                    ))
                )}
              </div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {(groups ?? []).length === 0 ? (
                  <div style={{ color: "#69727d" }}>{t(lang, "none")}</div>
                ) : (
                  (groups ?? []).map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => {
                        setExpensePickerOpen(false);
                        startExpenseFlow(g.id);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px",
                        borderRadius: 14,
                        border: theme === "dark" ? "1px solid #273244" : "1px solid #dde4ec",
                        background: theme === "dark" ? "#0e1116" : "#fff",
                        color: theme === "dark" ? "#e7ecf3" : "#111",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 16,
                          background: "#e8f3ec",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 900,
                          color: "#0f8c57",
                          flex: "0 0 auto",
                        }}
                      >
                        {(g.name || "G").slice(0, 1).toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {g.name}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {route !== "dashboard" && route !== "group" && route !== "profile" && (
        <div className="swk-auth-container" style={{ margin: "18px auto 12px" }}>
          <div
            className="swk-auth-switcher"
            style={{
              background: theme === "dark" ? "#0e1116" : "#e8f3ec",
              border: theme === "dark" ? "1px solid #273244" : "1px solid #d6e7dd",
            }}
          >
            {(
              [
                { key: "login", label: t(lang, "login"), path: "/" },
                { key: "register", label: t(lang, "register"), path: "/register" },
                { key: "join-group", label: t(lang, "joinGroup"), path: "/join-group" },
              ] as const
            ).map((item) => (
              <button
                key={item.key}
                onClick={() => navigate(item.path)}
                disabled={route === item.key}
                style={{
                  padding: "10px 8px",
                  borderRadius: 12,
                  border: "none",
                  fontWeight: 800,
                  background: route === item.key ? "#0f8c57" : "transparent",
                  color: route === item.key ? "#fff" : theme === "dark" ? "#e7ecf3" : "#0f8c57",
                  opacity: route === item.key ? 1 : 0.9,
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {route === "login" && (
        <div className="swk-auth-container" style={{ margin: "0 auto", display: "grid", gap: 10 }}>
          <div style={cardStyle}>
            <form onSubmit={onLogin} style={{ display: "grid", gap: 12 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={labelStyle}>{t(lang, "email")}</span>
                <input
                  className="swk-input"
                  style={inputStyle}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder={t(lang, "enterEmail")}
                />
              </label>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={labelStyle}>{t(lang, "password")}</span>
                <input
                  className="swk-input"
                  style={inputStyle}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder={t(lang, "enterPassword")}
                />
              </label>
              <button type="submit" style={primaryButton}>
                {t(lang, "login")}
              </button>
            </form>
          </div>
        </div>
      )}

      {route === "register" && (
        <div className="swk-auth-container" style={{ margin: "0 auto", display: "grid", gap: 10 }}>
          <div style={cardStyle}>
            <form onSubmit={onRegister} style={{ display: "grid", gap: 12 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={labelStyle}>{t(lang, "inviteTokenLabel")}</span>
                <input className="swk-input" style={inputStyle} value={inviteToken} onChange={(e) => setInviteToken(e.target.value)} />
              </label>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={labelStyle}>{t(lang, "email")}</span>
                <input
                  className="swk-input"
                  style={inputStyle}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder={t(lang, "enterEmail")}
                />
              </label>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={labelStyle}>{t(lang, "passwordMin")}</span>
                <input
                  className="swk-input"
                  style={inputStyle}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder={t(lang, "enterPassword")}
                />
              </label>
              <button type="submit" style={primaryButton}>
                {t(lang, "register")}
              </button>
            </form>
          </div>
        </div>
      )}

      {route === "join-group" && (
        <div className="swk-auth-container" style={{ margin: "0 auto", display: "grid", gap: 10 }}>
          <div style={cardStyle}>
            <form onSubmit={onJoinGroup} style={{ display: "grid", gap: 12 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={labelStyle}>{t(lang, "groupInviteToken")}</span>
                <input className="swk-input" style={inputStyle} value={joinToken} onChange={(e) => setJoinToken(e.target.value)} />
              </label>
              <button type="submit" style={primaryButton} disabled={!authToken}>
                {t(lang, "joinGroup")}
              </button>
              {!authToken && <div style={{ fontSize: 12, color: theme === "dark" ? "#9fb3d1" : "#69727d" }}>{t(lang, "loginFirst")}</div>}
            </form>
          </div>
        </div>
          )}

          {contactEditModal.id && (
            <div
              data-testid="contact-edit-modal"
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                zIndex: 2000,
              }}
            >
              <div style={{ background: "#fff", borderRadius: 14, padding: 16, width: "100%", maxWidth: 420, boxShadow: "0 16px 40px rgba(0,0,0,0.2)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <div style={{ fontWeight: 800, fontSize: 18 }}>{t(lang, "edit")}</div>
                  <button
                    onClick={() => setContactEditModal({ id: null, name: "", phone: "", email: "" })}
                    style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer" }}
                  >
                    ✕
                  </button>
                </div>
                <label style={{ display: "grid", gap: 6, marginBottom: 10 }}>
                  <span style={labelStyle}>{t(lang, "localDisplayName")}</span>
                  <input
                    style={inputStyle}
                    value={contactEditModal.name}
                    onChange={(e) => setContactEditModal((m) => ({ ...m, name: e.target.value }))}
                    placeholder={t(lang, "localDisplayName")}
                  />
                </label>
                <label style={{ display: "grid", gap: 6, marginBottom: 10 }}>
                  <span style={labelStyle}>{t(lang, "email")}</span>
                  <input
                    style={inputStyle}
                    value={contactEditModal.email}
                    onChange={(e) => setContactEditModal((m) => ({ ...m, email: e.target.value }))}
                    placeholder={t(lang, "email")}
                  />
                </label>
                <label style={{ display: "grid", gap: 6, marginBottom: 10 }}>
                  <span style={labelStyle}>{t(lang, "phone")}</span>
                  <input
                    style={inputStyle}
                    value={contactEditModal.phone}
                    onChange={(e) => setContactEditModal((m) => ({ ...m, phone: e.target.value }))}
                    placeholder={t(lang, "phone")}
                  />
                </label>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button
                    data-testid="contact-edit-save"
                    style={{ ...primaryButton, width: "auto", padding: "10px 16px" }}
                    onClick={() => {
                      if (!contactEditModal.id) return;
                      setContactSaving((s) => ({ ...s, [contactEditModal.id as string]: true }));
                      updateContact(contactEditModal.id, {
                        email: contactEditModal.email.trim() || null,
                        local_display_name: contactEditModal.name.trim() || null,
                        phone: contactEditModal.phone.trim() || null,
                      })
                        .then(() => refreshContacts())
                        .catch((err) => alert(String(err)))
                        .finally(() => {
                          setContactSaving((s) => ({ ...s, [contactEditModal.id as string]: false }));
                          setContactEditModal({ id: null, name: "", phone: "", email: "" });
                        });
                  }}
                  >
                    {t(lang, "save")}
                  </button>
                  <button
                    data-testid="contact-edit-cancel"
                    style={{ ...primaryButton, width: "auto", padding: "10px 16px", background: "#e5e8ed", color: "#111" }}
                    onClick={() => setContactEditModal({ id: null, name: "", phone: "", email: "" })}
                  >
                    {t(lang, "cancel")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {status && (
            <pre
              style={{
            maxWidth: 420,
            margin: "14px auto 0",
            whiteSpace: "pre-wrap",
            color: "#b00",
            background: "#fff3f3",
            borderRadius: 14,
            padding: 12,
            boxShadow: cardShadow,
          }}
        >
          {status}
        </pre>
      )}
    </div>
  );
}
